/**
 * Booking Chat real-time – Ably.
 * Subscribes to booking:${bookingId} (messages, typing) and user:${userId} (notifications).
 * Typing is sent via REST: POST /api/v1/customers/bookings/:bookingId/typing.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { getAblyRealtime, subscribeToChannel, isAblyConnected } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const BOOKING_CHANNEL_EVENTS = ['booking_message:new', 'booking:typing', 'booking_message:read'] as const;
const USER_NOTIFICATION_EVENTS = ['booking_message:notification'] as const;

export class BookingChatWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubBooking: (() => void) | null = null;
  private unsubUser: (() => void) | null = null;
  private currentBookingId: string | null = null;
  private userId: string | null = null;
  private lastMessageId: string | null = null;
  private lastMessageTime: number = 0;
  private connCleanup: (() => void) | null = null;

  connect(bookingId: string): void {
    if (this.currentBookingId === bookingId && (this.unsubBooking || this.unsubUser)) return;
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[BookingChatWebSocket] No access token');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[BookingChatWebSocket] Could not extract userId');
      return;
    }

    this.currentBookingId = bookingId;
    this.userId = uid;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[BookingChatWebSocket] handler error', e);
        }
      });
    };

    const processMessage = (data: any) => {
      const id = data?.id;
      const now = Date.now();
      if (id && id === this.lastMessageId && now - this.lastMessageTime < 500) return;
      this.lastMessageId = id ?? null;
      this.lastMessageTime = now;
      emit('booking_chat:message', data);
    };

    this.unsubBooking = subscribeToChannel(
      `booking:${bookingId}`,
      BOOKING_CHANNEL_EVENTS as unknown as readonly string[],
      (event, data) => {
        if (event === 'booking_message:new') processMessage(data);
        else if (event === 'booking:typing') emit('booking_chat:typing', data);
        else if (event === 'booking_message:read') emit('booking_chat:read', data);
      }
    );

    this.unsubUser = subscribeToChannel(
      `user:${uid}`,
      USER_NOTIFICATION_EVENTS as unknown as readonly string[],
      (event, data) => {
        if (event === 'booking_message:notification') emit('booking_message:notification', data);
      }
    );

    // Reflect the REAL Ably connection state so the chat header shows an honest 🟢/🔴 and re-syncs on
    // every reconnect (not just the first connect). 'booking_chat:joined' re-fires on reconnect; a
    // drop/suspension fires 'disconnect' so the UI stops showing a false-green while offline. The 5s
    // message poll (in BookingChat) is the safety net that backfills anything missed during the gap.
    const conn = getAblyRealtime().connection;
    const onConn = () => emit('booking_chat:joined', { bookingId });
    const onDrop = () => emit('disconnect', { bookingId });
    conn.on('connected', onConn);
    conn.on('disconnected', onDrop);
    conn.on('suspended', onDrop);
    if (conn.state === 'connected') emit('booking_chat:joined', { bookingId });
    this.connCleanup = () => {
      try { conn.off('connected', onConn); conn.off('disconnected', onDrop); conn.off('suspended', onDrop); } catch { /* ignore */ }
    };
  }

  leaveBookingChat(bookingId: string): void {
    if (this.currentBookingId === bookingId) this.disconnect();
  }

  startTyping(bookingId: string, _senderType: 'customer' | 'organizer' = 'customer'): void {
    apiService.sendBookingChatTyping(bookingId, true).catch(() => {});
  }

  stopTyping(bookingId: string, _senderType: 'customer' | 'organizer' = 'customer'): void {
    apiService.sendBookingChatTyping(bookingId, false).catch(() => {});
  }

  sendTyping(bookingId: string, isTyping: boolean): void {
    apiService.sendBookingChatTyping(bookingId, isTyping).catch(() => {});
  }

  sendMessage(_bookingId: string, _messageText: string): void {
    console.warn('[BookingChatWebSocket] sendMessage via socket is deprecated. Use REST API instead.');
  }

  markAsRead(_bookingId: string, _messageIds?: string[]): void {
    console.warn('[BookingChatWebSocket] markAsRead via socket is deprecated. Use REST API instead.');
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return (this.unsubBooking != null || this.unsubUser != null) && this.currentBookingId != null && isAblyConnected();
  }

  disconnect(): void {
    if (this.unsubBooking) {
      this.unsubBooking();
      this.unsubBooking = null;
    }
    if (this.unsubUser) {
      this.unsubUser();
      this.unsubUser = null;
    }
    if (this.connCleanup) { this.connCleanup(); this.connCleanup = null; }
    this.currentBookingId = null;
    this.userId = null;
    this.eventHandlers.clear();
  }
}

export const bookingChatWebSocketService = new BookingChatWebSocketService();
